import { useEffect, useState } from 'react';
import {
    Combobox,
    ComboboxInput,
    ComboboxList,
    ComboboxOption,
    ComboboxPopover,
    Typography,
} from '@vp/swan';
import { bugtracker, useIdentityContext } from '@99designs/design-services-common';
import { __ } from '@99designs/i18n';
import { sendProductSelectedTrackingEvent, usePage } from '@99designs/tracking';
import { useCart } from '../../../../../lib/hooks/useCart';
import { useBriefContext } from '../../../../BriefContext/BriefContext';
import { useBriefFormContext } from '../../../../BriefContext/BriefFormContext';
import { Field_ClientConfiguredField_Fragment } from '../../../brief.generated';
import useUpdateProductDropdown from '../ProductDropdown/useUpdateProductDropdown';
import { useProductOptions } from '../ProductOptions';
import { ClientConfiguredFieldProps } from '../types';
import {
    ExpertServicePrintProductsQueryResult,
    useExpertServicePrintProductsQuery,
} from './products.generated';

type ExpertServicePrintProduct = NonNullable<
    ExpertServicePrintProductsQueryResult['data']
>['expertServices'][0];

export function Input({
    id,
    clientConfiguredValue,
    groupId,
    setValue,
    register,
    clearErrors,
}: ClientConfiguredFieldProps) {
    const { locale, pageData } = usePage();
    const { setProductOptionTouched, setSelectedOptions } = useProductOptions();
    const { authorizationHeader, shopperId, isSignedIn } = useIdentityContext();
    const {
        product: productFromBriefContext,
        isUpdatingProduct,
        setIsUpdatingProduct,
        serviceProductMpvId,
    } = useBriefContext();
    const { briefId } = useBriefFormContext();
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isDisabled, setIsDisabled] = useState<boolean>(false);
    const [selectedKey, setSelectedKey] = useState<string>(clientConfiguredValue.toLowerCase());
    const [printProducts, setPrintProducts] = useState<ExpertServicePrintProduct[]>([]);

    // MUTATIONS
    const updateMutation = useUpdateProductDropdown(
        id,
        briefId,
        clientConfiguredValue,
        groupId,
        serviceProductMpvId
    );

    // QUERIES
    const { data, loading: isExpertServiceQueryLoading } = useExpertServicePrintProductsQuery({
        variables: { mpvId: serviceProductMpvId },
        skip: !serviceProductMpvId,
    });
    const { isInCart } = useCart(locale, { authorizationHeader, shopperId });

    useEffect(() => {
        async function disableIfBriefIsInCart() {
            const briefInCart = await isInCart(briefId as string);
            setIsDisabled(briefInCart);
        }
        if (isSignedIn) {
            void disableIfBriefIsInCart();
        }
    }, [briefId, isInCart, isSignedIn]);

    // LOGIC

    useEffect(() => {
        if (data?.expertServices && !isLoading) {
            if (data.expertServices.length === 0) {
                bugtracker.notify(new Error('No print products found for expert service'));
            }

            setPrintProducts(data.expertServices);
        }
    }, [data, isLoading]);

    useEffect(() => {
        setIsLoading(isExpertServiceQueryLoading || isUpdatingProduct);
    }, [isExpertServiceQueryLoading, isUpdatingProduct]);

    useEffect(() => {
        setValue(id, selectedKey);
        clearErrors(id);
    }, [id, selectedKey, setValue, clearErrors]);

    // Check if the product in brief context matches the related print product and update selected key
    useEffect(() => {
        if (productFromBriefContext?.fulfilmentMpvId && !isLoading) {
            const briefProductMpvId = productFromBriefContext.fulfilmentMpvId.toLowerCase();
            // If the brief context product matches one of the related print products, ensure it's selected
            if (selectedKey !== briefProductMpvId) {
                setSelectedKey(briefProductMpvId);
            }
        }
    }, [productFromBriefContext, selectedKey, isLoading]);

    const handleChange = async (selection: string) => {
        // Don't update if the same product is already selected
        if (!selection || selection === selectedKey) {
            return;
        }

        setIsUpdatingProduct(true);

        const selectedProduct = printProducts.find(
            (product) => product.printProduct?.mpvId.toLowerCase() === selection
        );

        if (!selectedProduct || !selectedProduct.printProduct) {
            bugtracker.notify(new Error('cannot get selected product'), (event) => {
                event.addMetadata('debug info', 'mpvID', selection);
            });

            setIsUpdatingProduct(false);
            return null;
        }

        await updateMutation(selectedProduct.printProduct.mpvId);

        setSelectedOptions({});
        setProductOptionTouched();
        setSelectedKey(selection);

        setIsUpdatingProduct(false);

        sendProductSelectedTrackingEvent({
            ...pageData,
            category: 'Design Services',
        });

        return;
    };

    if (printProducts.length === 0 && !isLoading) {
        return (
            <Combobox mb="5" key="error-dropdown-msb">
                <ComboboxInput placeholder={__('Select product')} disabled />
            </Combobox>
        );
    }

    return (
        <Combobox
            mb="5"
            mx="2"
            {...register(id, {
                value: selectedKey,
                required: __('Please select a product type', {
                    _context: 'This is the error message for product selection',
                }),
            })}
            selectedKey={selectedKey}
            onSelectionChange={(selection) => handleChange((selection as string).toLowerCase())}
            menuTrigger="focus"
        >
            <ComboboxInput
                placeholder={__('Select product')}
                loadingShimmer={isLoading}
                disabled={isDisabled || isLoading}
                data-testid="product-dropdown"
                aria-required={true}
                aria-labelledby={id}
            />
            <ComboboxPopover>
                <ComboboxList>
                    {printProducts.map(({ printProduct }) => (
                        <ComboboxOption key={printProduct?.mpvId.toLowerCase()}>
                            {printProduct?.name}
                        </ComboboxOption>
                    ))}
                </ComboboxList>
            </ComboboxPopover>
        </Combobox>
    );
}

export function Review({
    clientConfiguredValue,
}: Field_ClientConfiguredField_Fragment): JSX.Element | null {
    const { product } = useBriefContext();

    const printMpvID = product?.fulfilmentMpvId || '';

    const { data, loading } = useExpertServicePrintProductsQuery({
        variables: { mpvId: printMpvID },
        skip: !printMpvID,
    });

    if (loading || !data?.expertServices || !clientConfiguredValue) {
        return null;
    }

    const { printProduct } = data.expertServices[0];

    if (!printProduct) {
        return null;
    }

    return <Typography fontSkin="body-standard">{printProduct?.name}</Typography>;
}
